import type { ViewRenderer } from ".";
import { SvgService } from "../../../services/SvgService";
import { RENDERING } from "../../../config/constants";

export const GRADIENT_COLOR_START = "--gradient-start";
export const GRADIENT_COLOR_END = "--gradient-end";

export const gradientRenderer: ViewRenderer = (
	svgElem,
	delaunayTriangles,
	canvas,
) => {
	const { points, selectedPoints, showVertices } = canvas;

	// Create a <defs> section if it doesn't exist
	let defs = svgElem.querySelector("defs");
	if (!defs) {
		defs = SvgService.createSvgElement("defs");
		svgElem.appendChild(defs);
	}

	// Use shared gradient instead of creating one per triangle
	const sharedGradientId = RENDERING.SHARED_GRADIENT_ID;

	// Check if shared gradient already exists
	if (!defs.querySelector(`#${sharedGradientId}`)) {
		const gradient = SvgService.createSvgElement("linearGradient");
		gradient.setAttribute("id", sharedGradientId);
		gradient.setAttribute("x1", RENDERING.GRADIENT_X1);
		gradient.setAttribute("y1", RENDERING.GRADIENT_Y1);
		gradient.setAttribute("x2", RENDERING.GRADIENT_X2);
		gradient.setAttribute("y2", RENDERING.GRADIENT_Y2);

		// Create gradient stops
		const startStop = SvgService.createSvgElement("stop");
		startStop.setAttribute("offset", RENDERING.GRADIENT_START_OFFSET);
		startStop.setAttribute("stop-color", canvas.gradient.startColor);

		const endStop = SvgService.createSvgElement("stop");
		endStop.setAttribute("offset", RENDERING.GRADIENT_END_OFFSET);
		endStop.setAttribute("stop-color", canvas.gradient.endColor);

		gradient.appendChild(startStop);
		gradient.appendChild(endStop);
		defs.appendChild(gradient);
	} else {
		// Update existing gradient colors
		const existingGradient = defs.querySelector(
			`#${sharedGradientId}`,
		) as SVGLinearGradientElement;
		const stops = existingGradient.querySelectorAll("stop");
		if (stops[0])
			stops[0].setAttribute("stop-color", canvas.gradient.startColor);
		if (stops[1])
			stops[1].setAttribute("stop-color", canvas.gradient.endColor);
	}

	// Create triangles using DocumentFragment for better performance
	const triangleElements: SVGPolygonElement[] = [];

	for (let i = 0; i < delaunayTriangles.length; i += 3) {
		const p1 = delaunayTriangles[i];
		const p2 = delaunayTriangles[i + 1];
		const p3 = delaunayTriangles[i + 2];

		const polygon = SvgService.createSvgElement("polygon");

		polygon.setAttribute(
			"points",
			`${points[p1][0]},${points[p1][1]} ${points[p2][0]},${points[p2][1]} ${points[p3][0]},${points[p3][1]}`,
		);

		// Use shared gradient
		polygon.setAttribute("fill", `url(#${sharedGradientId})`);
		triangleElements.push(polygon);
	}

	// Batch append all triangles
	SvgService.batchAppendChildren(svgElem, triangleElements);

	// Add individual point circles for selection (if enabled)
	if (showVertices) {
		const pointElements: SVGCircleElement[] = [];
		points.forEach((point, index) => {
			const pointCircle = SvgService.createSvgElement("circle");
			pointCircle.setAttribute("cx", point[0].toString());
			pointCircle.setAttribute("cy", point[1].toString());
			pointCircle.setAttribute("r", "4");
			pointCircle.setAttribute(
				"fill",
				selectedPoints.includes(index)
					? "#ff6b35"
					: "rgba(255, 255, 255, 0.8)",
			);
			pointCircle.setAttribute(
				"stroke",
				selectedPoints.includes(index) ? "#ff6b35" : "rgba(0, 0, 0, 0.6)",
			);
			pointCircle.setAttribute("stroke-width", "2");
			pointCircle.setAttribute("cursor", "pointer");
			pointCircle.dataset.pointIndex = index.toString();
			pointElements.push(pointCircle);
		});

		SvgService.batchAppendChildren(svgElem, pointElements);
	}
};
